import { subject } from '@casl/ability'
import { colors } from '@lmc/common/constants'
import type { Log } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { type FC, useCallback } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Actions from '../../../../actions'
import { gaEvent } from '../../../../common/services/analytics'
import { getNominative } from '../../../../common/utils'
import {
  Pagination,
  Spinner,
  TableSortIcon
} from '../../../../components/generic'
import Selectors from '../../../../selectors'
import LogsTableItem from './LogsTableItem'

interface Page {
  size: number
  number: number
}

interface LogsTableProps {
  count?: number
  logs?: Log[]
  onChange: () => void
  page: Page
  preferredPeopleNominative?: string
  role: string
  setPageNumber: (number: number) => void
  setPageSize: (size: number) => void
  setSortType: (sortType: string) => void
  sort: string
  testId?: string
  loading?: boolean
  hasAccess: (abilities: any[]) => boolean
}

export const LogsTable: FC<LogsTableProps> = ({
  count,
  logs,
  onChange,
  page,
  preferredPeopleNominative,
  setPageNumber,
  setPageSize,
  setSortType,
  sort,
  testId,
  loading,
  hasAccess
}) => {
  const handleGaEvent = useCallback(
    ({ action, label }: { action: string; label: string | number }) => {
      gaEvent({
        category: 'Logs Dashboard',
        action,
        label: `${label}`
      })
    },
    []
  )

  const handleChangePageNumber = useCallback(
    (number: number) => {
      window.scrollTo(0, 0)
      setPageNumber(number)
      onChange()
      handleGaEvent({
        action: 'Change Page Number',
        label: number
      })
    },
    [setPageNumber, onChange, handleGaEvent]
  )

  const handleChangePageSize = useCallback(
    (size: number) => {
      setPageSize(size)
      onChange()
      handleGaEvent({
        action: 'Change Page Size',
        label: size
      })
    },
    [setPageSize, onChange, handleGaEvent]
  )

  const handleChangeSort = useCallback(
    (sortType: string) => {
      const sortRegexp = new RegExp(sortType)
      if (sortRegexp.test(sort)) {
        const reverseSortRegexp = new RegExp(`-${sortType}`)
        const isReverseSorted = reverseSortRegexp.test(sort)
        if (!isReverseSorted) {
          setSortType(`-${sortType}`)
          onChange()
          return
        }
      }
      setSortType(sortType)
      onChange()
      handleGaEvent({
        action: 'Change Sort Type',
        label: sortType
      })
    },
    [sort, setSortType, onChange, handleGaEvent]
  )

  const renderPagination = useCallback(
    (displayOptions: Record<string, boolean> = {}) => {
      return (
        <Pagination
          // @ts-ignore
          count={count}
          displayOptions={{
            ...displayOptions,
            lastPage: false
          }}
          listId="logs"
          page={page}
          onChangePageNumber={handleChangePageNumber}
          onChangePageSize={handleChangePageSize}
        />
      )
    },
    [count, page, handleChangePageNumber, handleChangePageSize]
  )

  const renderTableHeader = useCallback(() => {
    return (
      <div>
        <HeadersContainer>
          <TimeLoggedHeaderContainer>
            <Sortable
              id="sort-timeLogged"
              onClick={() => handleChangeSort('timeLogged')}
            >
              Time Logged
              <SortIcon>
                <TableSortIcon
                  ascending={sort === '-timeLogged'}
                  descending={sort === 'timeLogged'}
                />
              </SortIcon>
            </Sortable>
          </TimeLoggedHeaderContainer>
          <ResidentHeaderContainer>
            {getNominative(preferredPeopleNominative, 'Resident')}
          </ResidentHeaderContainer>
          <LogHeaderContainer>Log</LogHeaderContainer>
        </HeadersContainer>
        <Divider />
      </div>
    )
  }, [preferredPeopleNominative, sort, handleChangeSort])

  if (!logs) return <Spinner />

  if (!logs.length) {
    return (
      <EmptyState
        title="Shoot... No data to show you"
        description={NO_LOGS_HEADING}
        icon={<NoDataIcon />}
      />
    )
  }

  return (
    <Wrapper data-testid={testId ? `${testId}-table` : undefined}>
      <PaginationContainer>
        {renderPagination({ count: true, pageSize: true })}
      </PaginationContainer>
      <TableContainer>
        {renderTableHeader()}
        {loading ? (
          <TableSpinner />
        ) : (
          logs.map((log, index) => {
            const hasRolePermission = hasAccess([
              {
                i: 'view',
                this: subject('Log', {
                  item: log.item?.id
                })
              }
            ])
            return (
              // @ts-ignore
              <LogsTableItem
                key={log.id}
                log={log}
                residentStyle={styles.resident}
                testId={testId}
                index={index}
                linkEnabled={hasRolePermission}
              />
            )
          })
        )}
      </TableContainer>
      <PaginationContainer>
        {renderPagination({ pageNumbers: true, pageSize: true })}
      </PaginationContainer>
    </Wrapper>
  )
}

const NO_LOGS_HEADING = 'No logs found in date range'

const styles = {
  resident: {
    width: '25%'
  }
}

const mapStateToProps = (state: any) => ({
  count: state.logs.count,
  loading: state.global.loading,
  logs: Selectors.populateLogs(state),
  page: state.logs.page,
  role: state.global.user.role,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  sort: state.global.sortTypes.logs,
  hasAccess: (abilities: any[]) =>
    // @ts-ignore
    Selectors.checkAbilityAccess(state, { abilities })
})

const mapDispatchToProps = (dispatch: any) => ({
  setPageNumber: (number: number) =>
    dispatch(Actions.setLogsPageNumber(number)),
  setPageSize: (size: number) => dispatch(Actions.setLogsPageSize(size)),
  setSortType: (sortType: string) =>
    dispatch(
      Actions.setSortType({
        key: 'logs',
        sortType
      })
    )
})

export default connect(mapStateToProps, mapDispatchToProps)(LogsTable)

const Wrapper = styled.div`
  padding: 24px;
  background-color: ${colors.white};
  border-radius: 15px;
  overflow-x: auto;
`

const Divider = styled.div`
  background: ${colors.grey};
  height: 3px;
  margin-top: 10px;
`

const HeadersContainer = styled.div`
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 600;
`

const LogHeaderContainer = styled.div`
  display: flex;
  width: 55%;
`

const ResidentHeaderContainer = styled.div`
  display: flex;
  margin-right: 12px;
  width: 25%;
`

const Sortable = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`

const SortIcon = styled.div`
  position: relative;
  bottom: 5px;
`

const TableContainer = styled.div`
  min-width: 700px;
  overflow-x: auto;
  padding: 24px 0px;
`

const TableSpinner = styled(Spinner)`
  padding-top: 50px;
  padding-bottom: 500px;
`

const TimeLoggedHeaderContainer = styled.div`
  display: flex;
  margin-right: 12px;
  width: 20%;
`

const PaginationContainer = styled.div``
